import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch, invalidateApiCache } from "../services/api";
import "./CoachFeedbackWidget.css";

const TABS = [
  {
    value: "bug",
    label: "Bug",
    mode: "feedback",
    subtitle: "Signale un bug bloquant ou un comportement incohérent.",
    placeholder: "Ex: sur la fiche joueur, le planning reste vide après assignation...",
  },
  {
    value: "idea",
    label: "Idée",
    mode: "feedback",
    subtitle: "Propose une amélioration produit ou métier.",
    placeholder: "Ex: ajouter une vue J-2 / J-1 pour le suivi rugby...",
  },
  {
    value: "assistant",
    label: "Aide IA",
    mode: "assistant",
    subtitle: "Pose une question sur l’usage du logiciel ou une fonctionnalité.",
    placeholder: "Ex: comment créer un groupe de travail et y affecter mes joueurs ?",
  },
];

const getTab = (value) => TABS.find((entry) => entry.value === value) || TABS[0];

export default function CoachFeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("bug");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [assistantAnswer, setAssistantAnswer] = useState("");
  const [assistantSuggestions, setAssistantSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const currentPage = useMemo(
    () => `${location.pathname}${location.search || ""}`,
    [location.pathname, location.search]
  );

  const currentTab = getTab(activeTab);

  const resetForm = () => {
    setActiveTab("bug");
    setMessage("");
    setStatus("");
    setAssistantAnswer("");
    setAssistantSuggestions([]);
  };

  const closeModal = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    if (!message.trim()) {
      setStatus(
        currentTab.mode === "assistant"
          ? "Décris ton besoin pour obtenir une réponse."
          : "Décris le bug ou l’idée."
      );
      return;
    }

    setSubmitting(true);
    setStatus("");
    setAssistantAnswer("");
    setAssistantSuggestions([]);

    try {
      if (currentTab.mode === "assistant") {
        const payload = await apiFetch("/coach/help-assistant", {
          method: "POST",
          body: {
            question: message.trim(),
            pageUrl: currentPage,
          },
        });
        setAssistantAnswer(payload?.answer || "Aucune réponse disponible.");
        setAssistantSuggestions(Array.isArray(payload?.suggestions) ? payload.suggestions : []);
        setStatus("Réponse générée.");
      } else {
        await apiFetch("/coach/feedback", {
          method: "POST",
          body: {
            type: activeTab,
            message: message.trim(),
            pageUrl: currentPage,
            userAgent:
              typeof navigator !== "undefined" ? navigator.userAgent : null,
          },
        });
        invalidateApiCache("/coach/feedback");
        setStatus("Merci, c'est enregistré.");
        setMessage("");
        window.setTimeout(() => {
          setOpen(false);
          resetForm();
        }, 800);
      }
    } catch (error) {
      setStatus(error?.message || "Envoi impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="coach-feedback-trigger"
        aria-label="Ouvrir le centre d’aide rapide"
        onClick={() => setOpen(true)}
      >
        ?
      </button>

      {open ? (
        <div
          className="coach-feedback-overlay"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="coach-feedback-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="coach-feedback-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="coach-feedback-header">
              <div>
                <h3 id="coach-feedback-title" className="coach-feedback-title">
                  Support rapide
                </h3>
                <p className="coach-feedback-subtitle">{currentTab.subtitle}</p>
              </div>
              <button
                type="button"
                className="coach-feedback-close"
                onClick={closeModal}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            <div className="coach-feedback-tabs" role="tablist" aria-label="Types de retour">
              {TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.value}
                  className={`coach-feedback-tab ${activeTab === tab.value ? "is-active" : ""}`}
                  onClick={() => {
                    setActiveTab(tab.value);
                    setStatus("");
                    setAssistantAnswer("");
                    setAssistantSuggestions([]);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <form className="coach-feedback-form" onSubmit={submitFeedback}>
              <textarea
                className="coach-feedback-input coach-feedback-textarea"
                rows={5}
                placeholder={currentTab.placeholder}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />

              <div className="coach-feedback-page">Page : {currentPage}</div>

              {assistantAnswer ? (
                <div className="coach-feedback-assistant-box">
                  <div className="coach-feedback-assistant-label">Réponse</div>
                  <div className="coach-feedback-assistant-answer">{assistantAnswer}</div>
                  {assistantSuggestions.length ? (
                    <div className="coach-feedback-suggestions">
                      {assistantSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          className="coach-feedback-suggestion"
                          onClick={() => setMessage(suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {status ? <div className="coach-feedback-status">{status}</div> : null}

              <div className="coach-feedback-actions">
                <button
                  type="button"
                  className="coach-feedback-button coach-feedback-button--secondary"
                  onClick={closeModal}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="coach-feedback-button coach-feedback-button--primary"
                  disabled={submitting}
                >
                  {submitting
                    ? currentTab.mode === "assistant"
                      ? "Analyse..."
                      : "Envoi..."
                    : currentTab.mode === "assistant"
                      ? "Demander"
                      : "Envoyer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
